import { Link, Navigate } from "react-router-dom";
import { Barbell, Lightning, TrendUp, Trophy, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const HERO_BG = "https://images.unsplash.com/photo-1770513649465-2c60c8039806?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHx3ZWlnaHQlMjBwbGF0ZXMlMjBneW0lMjBkYXJrfGVufDB8fHx8MTc4MzQ2ODcxNHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Barbell, title: "LOG EVERY LIFT", body: "Track exercises, sets, reps and weight. Your training log — never forgotten." },
  { icon: TrendUp, title: "SEE THE GAINS", body: "Personal records tracked automatically. Progress charts that don't lie." },
  { icon: Trophy, title: "COMPETE", body: "Weekly and monthly leaderboards. Stack your volume against the community." },
  { icon: Lightning, title: "SPOTTER AI", body: "Ask an AI coach for programming, form cues, and recovery advice — anytime." },
];

export default function Landing() {
  const { user, loading } = useAuth();
  if (!loading && user) return <Navigate to="/feed" replace />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Nav */}
      <header className="border-b border-[#27272A] px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center">
            <Barbell size={20} weight="bold" color="#0A0A0A" />
          </div>
          <span className="font-display text-3xl font-black tracking-tight">SPOTTER</span>
        </div>
        <div className="flex gap-2">
          <Link to="/login" data-testid="landing-login-link" className="btn-ghost text-xs">Sign in</Link>
          <Link to="/register" data-testid="landing-register-link" className="btn-primary text-xs hidden sm:inline-flex items-center">Get started</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grid-bg">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO_BG} alt="Dark gym" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/80 to-[#0A0A0A]" />

        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-24 md:py-40">
          <div className="max-w-3xl fade-in">
            <div className="inline-flex items-center gap-2 border border-[#27272A] px-3 py-1 mb-6">
              <div className="w-2 h-2 bg-[#FF3B30]" />
              <span className="text-xs uppercase tracking-widest font-mono text-zinc-400">Strava for lifters</span>
            </div>

            <h1 className="font-display text-6xl md:text-8xl lg:text-9xl font-black uppercase leading-[0.9] tracking-tight mb-6">
              Lift.<br />
              Log.<br />
              <span className="text-[#FF3B30]">Level up.</span>
            </h1>

            <p className="text-lg md:text-xl text-zinc-300 max-w-xl mb-10 leading-relaxed">
              Spotter is the social gym log for people who take training seriously. Track your workouts, break your PRs, and climb the leaderboard.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register" className="btn-primary inline-flex items-center gap-2">
                Start lifting <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/login" data-testid="hero-cta-login" className="btn-ghost inline-flex items-center">
                I have an account
              </Link>
            </div>

            <div className="flex items-center gap-8 mt-16">
              <div>
                <div className="font-display text-4xl font-black text-white">100%</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Free forever</div>
              </div>
              <div className="w-px h-12 bg-[#27272A]" />
              <div>
                <div className="font-display text-4xl font-black text-white">AI</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Powered coach</div>
              </div>
              <div className="w-px h-12 bg-[#27272A] hidden sm:block" />
              <div className="hidden sm:block">
                <div className="font-display text-4xl font-black text-white">∞</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Workouts logged</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-20">
        <div className="mb-12">
          <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-mono mb-2">// The stack</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase">Everything you need. Nothing you don't.</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-[#0A0A0A] p-8 hover:bg-[#121212] transition-colors">
              <f.icon size={32} weight="bold" className="text-[#FF3B30] mb-4" />
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">{f.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272A] px-4 md:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="font-display text-xl font-black">SPOTTER</div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Built for the iron.</div>
      </footer>
    </div>
  );
}
